'use client'
/* eslint-disable react-hooks/exhaustive-deps */

import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { useLocale } from '@/components/LocaleContext'
import type { Localization } from '@/payload-types'
import { DIMENSION_FILTERS, SEARCH, SEARCH_TYPES } from '@/utilities/constant'
import { getLocalizedTranslationData } from '@/utilities/translationsData'
import {
  DimensionFieldConfig,
  FilterDimensionMappingType,
  transformDimensionsConfig,
} from './helper'

type DimensionState = {
  value: string
  tolerance?: number
}

const DEFAULT_TOLERANCE = 5

export function DynamicDimensionSearchBar() {
  const { locale, translatedData } = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()

  const commonT = (key: keyof Localization['common'], enFallback: string) =>
    translatedData?.common?.[key] ??
    getLocalizedTranslationData(locale ?? 'en').common?.[key] ??
    enFallback

  const toastMessages = (key: keyof Localization['toastMessages'], enFallback: string) =>
    translatedData?.toastMessages?.[key] ??
    getLocalizedTranslationData(locale ?? 'en').toastMessages?.[key] ??
    enFallback

  const [dimensions, setDimensions] = useState<Record<string, DimensionState>>({})
  const [selectedFilterType, setSelectedFilterType] = useState('')
  const [selectedFilterShape, setSelectedFilterShape] = useState('')
  const [filterDimensionMapping, setFilterDimensionMapping] = useState<FilterDimensionMappingType>(
    {},
  )
  const [dimensionApi, setDimensionApi] = useState<'loading' | 'error' | 'success'>('loading')
  const [selectOptions, setSelectOptions] = useState<Record<string, string[]>>({})

  const currentConfig = useMemo(() => {
    return filterDimensionMapping[selectedFilterType]?.[selectedFilterShape]
  }, [filterDimensionMapping, selectedFilterType, selectedFilterShape])

  const dimKeys = currentConfig ? Object.values(currentConfig.filters) : []
  const imageSrc = currentConfig?.image ?? '/dimensions/design-sample-abh.png'

  const filterTypes = useMemo(() => {
    return Object.keys(filterDimensionMapping)
  }, [filterDimensionMapping])

  // Available shapes for current type
  const availableShapes = useMemo(() => {
    return filterDimensionMapping[selectedFilterType]
      ? Object.keys(filterDimensionMapping[selectedFilterType])
      : []
  }, [filterDimensionMapping, selectedFilterType])

  const searchDataSet = useCallback(() => {
    const params = Object.fromEntries(searchParams.entries())
    const newDimensions: Record<string, DimensionState> = {}

    // Init filter type/shape from params or default
    const firstType = Object.keys(filterDimensionMapping)[0] || ''

    const paramType = params.filterType || firstType

    const firstShape = Object.keys(filterDimensionMapping[paramType] ?? {})[0] || ''

    const paramShape = params.filterShape || firstShape
    setSelectedFilterType(paramType)
    setSelectedFilterShape(paramShape)
    // Init dimensions from all possible dim params
    Object.entries(params).forEach(([key, value]) => {
      if (DIMENSION_FILTERS.map((d) => d.value).includes(key)) {
        const tolKey = key + 'tol'
        const tol = params[tolKey] ? Number(params[tolKey]) : DEFAULT_TOLERANCE
        newDimensions[key] = { value: String(value || ''), tolerance: tol }
      }
    })

    setDimensions(newDimensions)
  }, [searchParams, filterDimensionMapping]) // Re-run when searchParams or filterDimensionMapping changes

  const fetchSelectOptions = async (field: string, filterType: string, filterShape: string) => {
    try {
      const params = new URLSearchParams({
        field,
        filterType,
        filterShape,
      })

      const res = await fetch(`/api/products/dimension-options?${params}`)

      if (!res.ok) {
        throw new Error('Failed fetching options')
      }

      const data = await res.json()

      setSelectOptions((prev) => ({
        ...prev,
        [field]: data.values || [],
      }))
    } catch (err) {
      console.error('Fetching select option:', err)
    }
  }

  useEffect(() => {
    async function fetchDimensionsConfig() {
      try {
        const res = await fetch('/api/globals/dimensionsConfig')
        if (!res.ok) throw new Error(`Dimensions API failed: ${res.status}`)
        const dimensionsConfig = await res.json()
        if (dimensionsConfig?.dimensions) {
          setDimensionApi('success')
          setFilterDimensionMapping(transformDimensionsConfig(dimensionsConfig.dimensions))
          return
        }
        throw new Error('Dimensions configuration missing')
      } catch {
        setDimensionApi('error')
        toast.error(commonT('dimensions_config_error', 'Failed to load dimensions configuration'))
      }
    }
    fetchDimensionsConfig()
    searchDataSet() // Initial set from URL on mount
  }, [])

  useEffect(() => {
    searchDataSet()
  }, [searchParams, filterDimensionMapping])

  useEffect(() => {
    if (!currentConfig) return

    Object.values(currentConfig.filters).forEach((config) => {
      if (config.fieldType === 'select') {
        fetchSelectOptions(config.key, selectedFilterType, selectedFilterShape)
          .then(() =>{})
          .catch((err) => {
            console.error('Error fetching options:', err)
          })
      }
    })
  }, [currentConfig, selectedFilterType, selectedFilterShape])

  const updateValue = useCallback(
    (dimKey: string, value: string, fieldType: 'text' | 'number' | 'select') => {
      setDimensions((prev) => ({
        ...prev,
        [dimKey]: {
          value,
          tolerance:
            fieldType === 'number' ? (prev[dimKey]?.tolerance ?? DEFAULT_TOLERANCE) : undefined,
        },
      }))
    },
    [],
  )

  const updateTolerance = useCallback((dimKey: string, delta: number) => {
    setDimensions((prev) => ({
      ...prev,
      [dimKey]: {
        ...prev[dimKey],
        tolerance: Math.max(
          0,
          Math.min(10, (prev[dimKey]?.tolerance || DEFAULT_TOLERANCE) + delta),
        ),
      },
    }))
  }, [])

  const handleFilterTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value
    setSelectedFilterType(newType)
    const firstShape = Object.keys(filterDimensionMapping[newType] ?? {})[0] || ''
    setSelectedFilterShape(firstShape)
  }

  const handleFilterShapeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFilterShape(e.target.value)
  }

  const handleSearch = () => {
    const params = new URLSearchParams()
    params.set('type', SEARCH.DIMENSION)
    params.set('filterType', selectedFilterType)
    params.set('filterShape', selectedFilterShape)
    let hasError = false
    dimKeys.forEach((key) => {
      const dim = dimensions[key.key]
      if (dim?.value) {
        params.set(key.key, dim.value)
        const config = currentConfig?.filters[key.key]

        if (config?.fieldType === 'number' && dim.tolerance !== undefined) {
          params.set(`${key.key}tol`, dim.tolerance.toString())
        }
      } else {
        if (currentConfig?.filters[key.key].required) {
          toast.error(toastMessages('required_field_missing', 'Please fill all required fields'))
          hasError = true
          document.getElementById(key.key)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          document.getElementById(key.key)?.focus()
          return
        }
      }
    })
    const qKeep = (searchParams.get('q') ?? '').trim()
    if (qKeep) params.set('q', qKeep)
    if (!hasError) {
      router.push(`/products/search?${params.toString()}`, { scroll: false })
    }
  }

  const handleReset = () => {
    const params = new URLSearchParams()
    params.set('type', SEARCH.DIMENSION)
    const newDimensions: Record<string, DimensionState> = {}
    dimKeys.forEach((key) => {
      const config = currentConfig?.filters[key.key]

      newDimensions[key.key] = {
        value: '',
        tolerance: config?.fieldType === 'number' ? DEFAULT_TOLERANCE : undefined,
      }
    })
    setDimensions(newDimensions)
    router.push(`/products/search?${params.toString()}`, { scroll: false })
  }

  const renderInput = (config: DimensionFieldConfig) => {
    const dimKey = config.key

    const dim = dimensions[dimKey] || {
      value: '',
      tolerance: config.fieldType === 'number' ? DEFAULT_TOLERANCE : undefined,
    }

    return (
      <div key={dimKey} className="my-2 py-1 grid items-center gap-1">
        <div className="grid grid-cols-2 gap-1">
          <div className="flex flex-col gap-3 items-center justify-between">
            <label
              htmlFor={dimKey}
              className="text-xs md:text-base uppercase font-bold text-neutral-500 text-left rtl:text-right w-full"
            >
              {config.label}
              {config.required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {config.fieldType === 'number' && (
              <input
                id={dimKey}
                name={dimKey}
                type="number"
                value={dim.value}
                min={0}
                max={2000}
                required={config.required}
                onChange={(e) => updateValue(dimKey, e.target.value, config.fieldType)}
                className="w-full border border-neutral-200 px-2 py-1 text-sm"
                placeholder={config.label}
              />
            )}

            {config.fieldType === 'text' && (
              <input
                id={dimKey}
                name={dimKey}
                type="text"
                value={dim.value}
                required={config.required}
                onChange={(e) => updateValue(dimKey, e.target.value, config.fieldType)}
                className="w-full border border-neutral-200 px-2 py-1 text-sm"
                placeholder={config.label}
              />
            )}

            {config.fieldType === 'select' && (
              <select
                id={dimKey}
                name={dimKey}
                value={dim.value}
                required={config.required}
                onChange={(e) => updateValue(dimKey, e.target.value, config.fieldType)}
                className="w-full border border-neutral-200 px-2 py-1 text-sm"
              >
                <option value="">Select {config.label}</option>

                {selectOptions[dimKey]?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}
          </div>

          {config.fieldType === 'number' && (
            <div className="flex flex-col gap-3 items-center justify-between">
              <label
                htmlFor={`${dimKey}tol`}
                className="block text-xs md:text-base text-neutral-500 text-left rtl:text-right"
              >
                Tolerance
              </label>

              <div className="flex items-center gap-2 h-7">
                <button
                  onClick={() => updateTolerance(dimKey, -1)}
                  className="bg-red-500 text-white px-2 py-0 h-6 disabled:bg-gray-400"
                  disabled={!!dim.tolerance && dim.tolerance <= 0}
                >
                  −
                </button>

                <span className="text-sm min-w-[2rem] text-center">{dim.tolerance} MM</span>

                <button
                  onClick={() => updateTolerance(dimKey, 1)}
                  className="bg-red-500 text-white px-2 py-0 h-6 disabled:bg-gray-400"
                  disabled={!!dim.tolerance && dim.tolerance >= 10}
                >
                  +
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const handleSelectType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const p = new URLSearchParams()
    p.set('type', e.target.value)
    const q = (searchParams.get('q') ?? '').trim()
    if (q) p.set('q', q)
    router.push(`/products/search?${p.toString()}`, { scroll: false })
  }

  const searchTypeOptions: { value: (typeof SEARCH_TYPES)[number]; label: string }[] = [
    { value: SEARCH_TYPES[0], label: commonT('smart_search', SEARCH.SMART) },
    { value: SEARCH_TYPES[1], label: commonT('cross_reference', SEARCH.CROSS) },
    { value: SEARCH_TYPES[2], label: commonT('dimension_search', SEARCH.DIMENSION) },
  ]

  const normalizeDimensionBarSearchType = (type: string | null) => {
    const raw = (type ?? '').trim()
    if (SEARCH_TYPES.includes(raw as (typeof SEARCH_TYPES)[number])) {
      return raw as (typeof SEARCH_TYPES)[number]
    }
    const found = searchTypeOptions.find((opt) => opt.label.toLowerCase() === raw.toLowerCase())
    return found?.value ?? SEARCH_TYPES[2]
  }

  const dimensionBarSearchType = normalizeDimensionBarSearchType(searchParams.get('type'))
  const shouldSplit = dimKeys.length > 5
  const leftFields = shouldSplit ? dimKeys.slice(0, Math.ceil(dimKeys.length / 2)) : dimKeys
  const rightFields = shouldSplit ? dimKeys.slice(Math.ceil(dimKeys.length / 2)) : []

  return (
    <section
      className="w-full bg-neutral-100 py-4 sm:py-5 transition-opacity duration-300"
      aria-label={commonT('search', 'Search')}
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 relative min-h-[650px]">
        {dimensionApi === 'loading' && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-neutral-100/80 backdrop-blur-[1px] transition-opacity duration-300">
            <span className="text-neutral-500 animate-pulse">
              {commonT('loading', 'Loading...')}
            </span>
          </div>
        )}

        {dimensionApi === 'error' && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-neutral-100/80">
            <span className="text-red-500">
              {commonT('dimensions_config_error', 'Failed to load dimensions configuration')}
            </span>
          </div>
        )}

        <div
          className={`transition-all duration-500 ${
            dimensionApi === 'success'
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-2 pointer-events-none'
          }`}
        >
          {/* Top filters */}
          <div className="grid sm:grid-cols-3 grid-cols-1 gap-4 mb-6 rounded-md bg-white shadow-md">
            <div className="flex flex-1 flex-col border-b border-neutral-200 py-3 pl-4 pr-3 sm:border-b-0 sm:border-r sm:py-4 sm:pr-4">
              <label className="block text-xs text-neutral-500 pl-1 uppercase">
                {commonT('search_type', 'Search type')}
              </label>
              <select
                className="w-full border-none pr-2 py-2 bg-transparent leading-relaxed outline-none focus:outline-none focus:ring-0 focus:border-neutral-300"
                value={dimensionBarSearchType}
                onChange={handleSelectType}
              >
                {searchTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} className="py-2 leading-relaxed">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-1 flex-col border-b border-neutral-200 py-3 pl-4 pr-3 sm:border-b-0 sm:border-r sm:py-4 sm:pr-4">
              <label className="block text-xs text-neutral-500 pl-1 uppercase">
                {commonT('filter_type', 'Filter Type')}
              </label>
              <select
                className="w-full border-none pr-2 py-2 bg-transparent placeholder:font-medium"
                value={selectedFilterType}
                onChange={handleFilterTypeChange}
                unselectable="on"
              >
                {filterTypes?.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-1 flex-col py-3 pl-4 pr-3 sm:py-4 sm:pl-5 sm:pr-4">
              <label className="block text-xs text-neutral-500 pl-1 uppercase">
                {commonT('filter_shape', 'Filter Shape')}
              </label>
              <select
                className="w-full border-none pr-2 py-2 bg-transparent"
                value={selectedFilterShape}
                onChange={handleFilterShapeChange}
                disabled={!availableShapes.length}
                unselectable="on"
              >
                {availableShapes?.map((shape) => (
                  <option key={shape} value={shape}>
                    {shape}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Main layout */}
          <div
            className={`grid gap-6 items-start ${
              shouldSplit
                ? 'grid-cols-1 md:grid-cols-[2fr,1fr,2fr]'
                : 'grid-cols-1 md:grid-cols-[2fr,1fr]'
            }`}
          >
            {/* Left panel */}
            <div className="bg-white p-4 shadow pt-10">
              {leftFields.length ? (
                leftFields.map((key) => renderInput(key))
              ) : (
                <p className="text-neutral-500 text-sm">
                  {commonT('select_filter_type_shape', 'Select filter type and shape')}
                </p>
              )}
            </div>

            {/* Center image */}
            <div className="flex justify-center w-full">
              {imageSrc && (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={imageSrc}
                    alt="Filter diagram"
                    className="max-h-64 md:max-h-full object-contain"
                  />
                </>
              )}
            </div>

            {/* Right panel */}
            {shouldSplit && (
              <div className="bg-white p-4 shadow pt-10">
                {rightFields?.map((key) => renderInput(key))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={handleSearch}
              className="bg-red-600 text-white px-10 py-3 font-semibold hover:bg-red-700"
              disabled={!dimKeys.some((key) => dimensions[key.key]?.value)}
            >
              {commonT('search', 'Search')}
            </button>
            <button
              onClick={handleReset}
              className="bg-black text-white px-10 py-3 font-semibold hover:bg-gray-800"
            >
              {translatedData?.common.reset ?? 'RESET'}
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
